import React, { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { Box, Button, IconButton, InputAdornment, OutlinedInput, Typography, FormHelperText } from '@mui/material';
import logo from '../assets/images/logoonly.png';
import bg from '../assets/background.jpg';
import { useAuthenticated } from './AuthContext';
import { useSnackbar } from 'notistack';
import { Eye, EyeSlash, Key, Login, Profile2User } from 'iconsax-react';
import AnimateButton from '../components/CustomInputs/AnimateButton';

export default function AuthLogin() {
  const { loginRoute, getUserById } = useAuthenticated();
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => event.preventDefault();

  return (
    <Grid
      container
      sx={{
        minHeight: '100vh',
        width: '100vw'
      }}
    >
      {/* Left - Blue part (7 columns) */}
      <Grid
        item
        xs={12}
        md={7}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: `#132037 url(${bg}) no-repeat center center`,
          backgroundSize: 'cover',
          minHeight: '100vh'
        }}
      >
        <img src={logo} alt="One Coherent Logo" style={{ width: 180, marginBottom: 20 }} />
        <div
          style={{
            fontSize: '2.7rem',
            color: '#fff',
            fontWeight: 600,
            marginTop: 24,
            letterSpacing: '.02em',
            fontFamily: 'Poppins, sans-serif'
          }}
        >
          One Coherent
        </div>
      </Grid>
      {/* Right - White part (5 columns) */}
      <Grid
        item
        xs={12}
        md={5}
        sx={{
          background: '#fff',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 0 24px 0 rgba(30,40,92,0.09)',
          padding: { xs: '40px 16px', md: '50px 105px' }
        }}
      >
        <img src={logo} alt="One Coherent Small" style={{ width: 55, marginBottom: 16 }} />
        <div
          style={{
            fontSize: '1.45rem',
            color: '#28395a',
            fontWeight: 600,
            marginBottom: 32,
            fontFamily: 'Poppins, sans-serif',
            textAlign: 'center'
          }}
        >
          One Coherent
        </div>
        <Formik
          initialValues={{ email: '', password: '' }}
          validationSchema={Yup.object({
            email: Yup.string().required('Employee Code required'),
            password: Yup.string().required('Password required')
          })}
          onSubmit={async (values, { setStatus, setSubmitting }) => {
            const userData = {
              employeeCode: values.email,
              password: values.password
            };
            try {
              if (values.email && values.password) {
                const response = await axios.post('https://cms-auth.coherent.in/auth/authenticate', userData, {
                  headers: { 'Content-Type': 'application/json' }
                });
                if (response?.data) {
                  const { accessToken, refreshToken } = response.data.data;
                  localStorage.setItem('accessToken', accessToken);
                  localStorage.setItem('refreshToken', refreshToken);
                  localStorage.setItem('isAuthenticated', 'true');
                  localStorage.setItem('oneCoherentIsTrue', 'false');
                  localStorage.setItem('oneCoherentLogin', 'true');
                  setStatus({ success: true });
                  enqueueSnackbar('Login successful!', { variant: 'success' });
                  getUserById(accessToken);
                  loginRoute('Super User', navigate);
                } else {
                  enqueueSnackbar('Login failed', { variant: 'error' });
                }
              } else {
                throw new Error('Please enter both');
              }
            } catch (err) {
              enqueueSnackbar('Login Failed, Please try again', {
                variant: 'error'
              });
              setStatus({
                error: err?.message || 'Login failed. Please try again.'
              });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
            <form noValidate onSubmit={handleSubmit} style={{ width: '100%' }}>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#263e57', mb: 1 }}>
                    Employee Code
                  </Typography>
                  <OutlinedInput
                    id="email"
                    type="text"
                    name="email"
                    value={values.email}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="Enter Employee Code"
                    fullWidth
                    error={Boolean(touched.email && errors.email)}
                    sx={{
                      borderRadius: '8px',
                      background: '#ffffff',
                      height: '47px',
                      color: '#263e57',
                      fontFamily: 'Poppins,sans-serif'
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <Profile2User size="20" variant="Bulk" color="#4E88ED" />
                      </InputAdornment>
                    }
                  />
                  {touched.email && errors.email && (
                    <FormHelperText error sx={{ marginLeft: 0 }}>
                      {errors.email}
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#263e57', mb: 1 }}>
                    Password
                  </Typography>
                  <OutlinedInput
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={values.password}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="Enter Password"
                    fullWidth
                    error={Boolean(touched.password && errors.password)}
                    sx={{
                      borderRadius: '8px',
                      background: '#ffffff',
                      height: '47px',
                      color: '#263e57',
                      fontFamily: 'Poppins,sans-serif'
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <Key size="20" variant="Bulk" color="#4E88ED" />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          sx={{
                            color: '#4884f3',
                            background: 'transparent',
                            p: 0.5
                          }}
                        >
                          {showPassword ? (
                            <Eye size="20" variant="Bulk" color="#4E88ED" />
                          ) : (
                            <EyeSlash size="20" variant="Bulk" color="#4E88ED" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {touched.password && errors.password && (
                    <FormHelperText error sx={{ marginLeft: 0 }}>
                      {errors.password}
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'right', mb: 2 }}>
                    <Button
                      sx={{
                        color: '#4195f6',
                        textTransform: 'none',
                        fontSize: '.97rem',
                        padding: 0,
                        minWidth: 0,
                        fontWeight: 500,
                        fontFamily: 'Poppins,sans-serif'
                      }}
                      onClick={() => navigate('/forgot-password')}
                    >
                      Forgot Password?
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <AnimateButton>
                    <Button
                      disableElevation
                      disabled={isSubmitting}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{
                        fontWeight: 500,
                        borderRadius: '8px',
                        fontSize: '1.15rem',
                        height: '47px',
                        background: '#4680FF',
                        color: '#fff',
                        boxShadow: 'none',
                        letterSpacing: '.01em',
                        display: 'flex',
                        alignItems: 'center',
                        fontFamily: 'Poppins,sans-serif'
                      }}
                      endIcon={<Login size="20" variant="Bulk" color="#ffffff" />}
                    >
                      Login
                    </Button>
                  </AnimateButton>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
}
